import { CalendarDays, CheckCircle2, MessageCircle, PartyPopper, Soup, Users } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { PageHero } from "../components/shared/PageHero";
import { PageMeta } from "../components/shared/PageMeta";
import { SectionHeading } from "../components/shared/SectionHeading";
import { WhatsAppLink } from "../components/shared/WhatsAppLink";
import { orderFaqs } from "../data/faqs";
import { openWhatsApp } from "../lib/whatsapp";

type OrderForm = {
  name: string;
  phone: string;
  email: string;
  type: string;
  date: string;
  people: string;
  message: string;
};

const initialForm: OrderForm = { name: "", phone: "", email: "", type: "", date: "", people: "", message: "" };

export function OrdersPage() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof OrderForm, string>>>({});
  const [popupBlocked, setPopupBlocked] = useState(false);

  const update = (field: keyof OrderForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setPopupBlocked(false);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Partial<Record<keyof OrderForm, string>> = {};
    if (form.name.trim().length < 2) nextErrors.name = "Indique o seu nome.";
    if (!/^\+?[0-9\s]{9,16}$/.test(form.phone.trim())) nextErrors.phone = "Indique um telemóvel válido.";
    if (!form.type) nextErrors.type = "Escolha o tipo de encomenda.";
    if (!form.date) nextErrors.date = "Indique a data pretendida.";
    if (!form.people || Number(form.people) < 1) nextErrors.people = "Indique o número aproximado de pessoas.";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Indique um email válido ou deixe o campo vazio.";
    if (form.message.trim().length < 5) nextErrors.message = "Dê-nos um pouco mais de informação sobre o pedido.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      window.requestAnimationFrame(() => document.querySelector<HTMLElement>("[aria-invalid='true']")?.focus());
      return;
    }

    const message = [
      "Olá! Gostaria de pedir informações para uma encomenda.",
      "",
      `Nome: ${form.name.trim()}`,
      `Telemóvel: ${form.phone.trim()}`,
      form.email ? `Email: ${form.email.trim()}` : "",
      `Tipo: ${form.type}`,
      `Data pretendida: ${form.date}`,
      `Número aproximado de pessoas: ${form.people}`,
      `Detalhes: ${form.message.trim()}`,
    ].filter(Boolean).join("\n");
    setPopupBlocked(!openWhatsApp(message));
  };

  return (
    <>
      <PageMeta title="Encomendas para Festas | Pastelaria Casagrande" description="Peça informações sobre salgados, refeições e encomendas para festas e eventos na Marinha Grande." path="/encomendas" />
      <PageHero eyebrow="Encomendas" title="Os seus momentos especiais, preparados com cuidado" description="Aniversários, reuniões, festas familiares ou uma refeição especial: diga-nos o que está a planear." imageSlot="ENCOMENDAS_FESTAS" imageAlt="Encomenda de salgados para festas">
        <WhatsAppLink message="Olá! Gostaria de pedir informações sobre uma encomenda para um evento.">Falar sobre uma encomenda</WhatsAppLink>
      </PageHero>

      <section className="section section--surface">
        <div className="container">
          <SectionHeading eyebrow="Para celebrar e partilhar" title="Encomendas à medida do seu momento" align="center" />
          <div className="order-types">
            {[
              { icon: PartyPopper, title: "Festas e aniversários", text: "Variedade de salgados e opções para partilhar." },
              { icon: Users, title: "Eventos familiares", text: "Preparações para reunir família e amigos à mesa." },
              { icon: Soup, title: "Refeições especiais", text: "Disponibilidade confirmada diretamente com a equipa." },
              { icon: CalendarDays, title: "Pedidos personalizados", text: "Conte-nos o que procura e analisamos consigo." },
            ].map(({ icon: Icon, title, text }) => <article key={title}><Icon aria-hidden="true" /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="section how-it-works">
        <div className="container">
          <SectionHeading eyebrow="Simples e próximo" title="Como funciona" description="A confirmação final é sempre feita diretamente com a nossa equipa." />
          <ol>
            {["Entre em contacto.", "Informe o tipo de evento e a quantidade aproximada.", "Consulte variedades e disponibilidade.", "Confirme a encomenda diretamente com a equipa."].map((step, index) => <li key={step}><span>{index + 1}</span><p>{step}</p></li>)}
          </ol>
        </div>
      </section>

      <section className="section section--cream">
        <div className="container form-layout">
          <div>
            <SectionHeading eyebrow="Prepare o seu pedido" title="Conte-nos o que está a planear" description="Preencha os dados e abriremos uma mensagem no WhatsApp. O pedido só fica confirmado depois de falar com a equipa." />
            <div className="form-trust"><CheckCircle2 aria-hidden="true" /><span>Sem envio automático nem dados guardados neste website.</span></div>
          </div>
          <form className="form-card" noValidate onSubmit={submit}>
            <FormField label="Nome" id="order-name" error={errors.name} required>
              <input id="order-name" value={form.name} onChange={(event) => update("name", event.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "order-name-error" : undefined} autoComplete="name" />
            </FormField>
            <div className="form-row">
              <FormField label="Telemóvel" id="order-phone" error={errors.phone} required>
                <input id="order-phone" type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? "order-phone-error" : undefined} autoComplete="tel" />
              </FormField>
              <FormField label="Email (opcional)" id="order-email" error={errors.email}>
                <input id="order-email" type="email" value={form.email} onChange={(event) => update("email", event.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "order-email-error" : undefined} autoComplete="email" />
              </FormField>
            </div>
            <FormField label="Tipo de encomenda" id="order-type" error={errors.type} required>
              <select id="order-type" value={form.type} onChange={(event) => update("type", event.target.value)} aria-invalid={Boolean(errors.type)} aria-describedby={errors.type ? "order-type-error" : undefined}>
                <option value="">Selecione uma opção</option><option>Salgados para festa</option><option>Aniversário</option><option>Evento familiar</option><option>Refeição especial</option><option>Pedido personalizado</option>
              </select>
            </FormField>
            <div className="form-row">
              <FormField label="Data pretendida" id="order-date" error={errors.date} required>
                <input id="order-date" type="date" min={new Date().toISOString().split("T")[0]} value={form.date} onChange={(event) => update("date", event.target.value)} aria-invalid={Boolean(errors.date)} aria-describedby={errors.date ? "order-date-error" : undefined} />
              </FormField>
              <FormField label="Número estimado de pessoas" id="order-people" error={errors.people} required>
                <input id="order-people" type="number" min="1" inputMode="numeric" value={form.people} onChange={(event) => update("people", event.target.value)} aria-invalid={Boolean(errors.people)} aria-describedby={errors.people ? "order-people-error" : undefined} />
              </FormField>
            </div>
            <FormField label="Mensagem" id="order-message" error={errors.message} required>
              <textarea id="order-message" rows={5} value={form.message} onChange={(event) => update("message", event.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "order-message-error" : undefined} placeholder="Indique quantidades aproximadas, preferências ou outras informações úteis." />
            </FormField>
            {popupBlocked && <p className="form-error" role="alert">O WhatsApp não abriu. Permita pop-ups neste site ou use o botão flutuante.</p>}
            <button type="submit" className="button button--whatsapp button--full"><MessageCircle aria-hidden="true" size={19} />Enviar pedido pelo WhatsApp</button>
            <p className="form-disclaimer">Ao continuar, os dados serão incluídos numa mensagem que poderá rever antes de enviar no WhatsApp.</p>
          </form>
        </div>
      </section>

      <section className="section section--surface">
        <div className="container faq-layout">
          <SectionHeading eyebrow="Perguntas frequentes" title="Antes de fazer a sua encomenda" />
          <div className="faq-list">{orderFaqs.map((faq) => <details key={faq.question}><summary>{faq.question}</summary><p>{faq.answer}</p></details>)}</div>
        </div>
      </section>
    </>
  );
}

type FormFieldProps = { label: string; id: string; error?: string; required?: boolean; children: ReactNode };
function FormField({ label, id, error, required, children }: FormFieldProps) {
  return <div className="form-field"><label htmlFor={id}>{label}{required && <span aria-hidden="true"> *</span>}</label>{children}{error && <p id={`${id}-error`} className="form-error">{error}</p>}</div>;
}
